
import { useState } from 'react';
import { toast } from 'sonner';
import { postComment } from '@/services/comments/createService';
import { Comment } from '@/types/social';

interface UseCommentSubmissionProps {
  contentId: string;
  contentType: 'poll' | 'card';
  userId: string | null;
  anonymousId: string | null;
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  setCommentCount: React.Dispatch<React.SetStateAction<number>>;
  setReplies: React.Dispatch<React.SetStateAction<Record<string, Comment[]>>>;
  setReplyForm: (commentId: string | null) => void;
}

export function useCommentSubmission({
  contentId,
  contentType,
  userId,
  anonymousId,
  setComments,
  setCommentCount,
  setReplies,
  setReplyForm
}: UseCommentSubmissionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitComment = async (content: string, parentId?: string | null) => {
    if (!content.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await postComment({
        content: content.trim(),
        contentId,
        contentType,
        userId,
        anonymousId,
        parentId
      });

      if (!result.success) {
        const errorMessage = typeof result.error === 'string' 
          ? result.error 
          : result.error?.message || 'Failed to post comment';
        throw new Error(errorMessage);
      }

      const newComment = result.data!;

      if (parentId) {
        // This is a reply - add it to the replies state
        setReplies(prev => ({
          ...prev,
          [parentId]: [...(prev[parentId] || []), newComment]
        }));
        
        // Close the reply form
        setReplyForm(null);
        
        toast.success('Reply added successfully');
      } else {
        // This is a top-level comment - add it to the main comments
        setComments(prev => [newComment, ...prev]);
        setCommentCount(prev => prev + 1);
        
        toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Failed to submit comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmitComment
  };
}
